/**
 * Ao carregar a app, verifica se há transações que foram enviadas mas o usuário
 * fechou/recarregou antes de ver o receipt. Se a tx estiver confirmada na chain,
 * registra a taxa pendente para não perder o registro do lado do pagador.
 */

import React, { useEffect, useRef } from 'react';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { usePendingFees } from '@/contexts/PendingFeeContext';
import { getPendingTxReconcileList, removePendingTxReconcile } from '@/utils/pendingTxReconcile';
import { getReadOnlyProvider } from '@/utils/publicRpcReadOnly';

export default function ReconcilePendingTx() {
  const { provider, connectedWallet, isPhantomEVM, walletChain } = useWalletConnection();
  const { addPendingFee, pendingFees } = usePendingFees();
  const didRunRef = useRef(false);
  const pendingFeesRef = useRef(pendingFees);
  pendingFeesRef.current = pendingFees;

  const isEVM =
    connectedWallet === 'metamask' ||
    connectedWallet === 'walletconnect' ||
    (connectedWallet === 'phantom' && isPhantomEVM);

  useEffect(() => {
    if (!provider || !isEVM || typeof addPendingFee !== 'function') return;
    if (didRunRef.current) return;
    didRunRef.current = true;

    const run = async () => {
      const list = getPendingTxReconcileList();
      if (list.length === 0) return;

      for (const entry of list) {
        try {
          const readOnlyProvider = getReadOnlyProvider(entry.chainId || walletChain);
          const receipt = await readOnlyProvider.getTransactionReceipt(entry.txHash);
          if (!receipt) continue;
          const status = receipt.status;
          if (status === '0x0' || status === 0) {
            removePendingTxReconcile(entry.txHash);
            continue;
          }
          if (status !== '0x1' && status !== 1) continue;
          const currentFees = pendingFeesRef.current || [];
          const alreadyAdded = currentFees.some(
            (f) => (f.originTxHash || '').toLowerCase() === (entry.txHash || '').toLowerCase(),
          );
          if (alreadyAdded) {
            removePendingTxReconcile(entry.txHash);
            continue;
          }
          if (entry.feeUSD && entry.feeUSD > 0) {
            addPendingFee({
              feeUSD: entry.feeUSD,
              token: entry.token || '',
              chainId: entry.chainId || '',
              networkName: entry.networkName || '',
              originTxHash: entry.txHash,
            });
          }
          removePendingTxReconcile(entry.txHash);
        } catch (_) {
          // Próxima carga pode tentar de novo
        }
      }
    };

    run();
  }, [provider, isEVM, addPendingFee]);

  return null;
}
